import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BookOpen, Brain, Calculator, AlertTriangle, CheckCircle2, Database } from "lucide-react";
import { Link } from "react-router-dom";

export default function DocsStmBudget() {
  return (
    <div className="container max-w-4xl py-8 space-y-8">
      <header className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Link to="/dashboard" className="hover:text-foreground">Dashboard</Link>
          <span>/</span>
          <Link to="/knowledge-base" className="hover:text-foreground">Documentação</Link>
          <span>/</span>
          <span className="text-foreground">STM Budget</span>
        </div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Brain className="h-8 w-8 text-primary" />
          STM Budget — `max_chars: 24k` e a regra dos 5%
        </h1>
        <p className="text-muted-foreground">
          Como a Memória de Curto Prazo (STM) da SYRA decide quantas mensagens
          recentes entram no contexto enviado ao modelo, a relação entre
          caracteres e tokens, e o que acontece quando o modelo escolhido não
          está na whitelist.
        </p>
      </header>

      {/* TL;DR */}
      <Card className="border-primary/40 bg-primary/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-primary" /> TL;DR
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p>
            <strong>STM ≠ janela do modelo.</strong> Janela do modelo é o
            máximo absoluto que ele aceita. STM é o pedaço dessa janela que a
            SYRA reserva especificamente para mensagens recentes do grupo.
          </p>
          <p>
            <strong>Default: 24.000 caracteres ≈ 6.000 tokens ≈ 300 mensagens
            curtas ≈ 48 horas.</strong> Quem mandar primeiro no clamp ganha.
          </p>
          <p>
            <strong>Regra dos 5%:</strong> STM efetiva ≤ 5% da janela do
            modelo, para nunca estourar prompt em modelo com janela pequena.
          </p>
        </CardContent>
      </Card>

      {/* O que é STM */}
      <section className="space-y-4">
        <h2 className="text-2xl font-semibold flex items-center gap-2">
          <Database className="h-6 w-6 text-primary" />
          O que é o STM Budget
        </h2>
        <p className="text-sm leading-relaxed">
          A SYRA mantém três limites independentes para decidir quais
          mensagens recentes entram no contexto de cada turno. Os três são
          aplicados em série: a primeira regra que truncar manda.
        </p>

        <div className="grid md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">max_chars</CardTitle>
              <CardDescription>Default 24.000</CardDescription>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground">
              Tamanho total do bloco de mensagens recentes em
              <strong> caracteres</strong>. É o limite que trava primeiro em
              conversas longas.
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">max_messages</CardTitle>
              <CardDescription>Default 300</CardDescription>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground">
              Quantas mensagens, no máximo, são lidas do banco por turno.
              Limite de leitura, não de envio.
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">max_age_hours</CardTitle>
              <CardDescription>Default 48</CardDescription>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground">
              Janela temporal. Mensagens mais antigas que isso entram só via
              memória de longo prazo (LTM), não em STM.
            </CardContent>
          </Card>
        </div>

        <p className="text-xs text-muted-foreground">
          Os três valores ficam em <code>cortex_feature_flags</code> nas
          chaves <code>memory.stm.max_chars</code>,
          <code> memory.stm.max_messages</code> e
          <code> memory.stm.max_age_hours</code>. O widget no Dashboard
          mostra os valores ativos.
        </p>
      </section>

      <Separator />

      {/* Tokens vs caracteres */}
      <section className="space-y-4">
        <h2 className="text-2xl font-semibold flex items-center gap-2">
          <Calculator className="h-6 w-6 text-primary" />
          Caracteres × tokens — por que medimos em chars
        </h2>
        <p className="text-sm leading-relaxed">
          Provedores de IA cobram e limitam por <strong>tokens</strong>, mas
          a SYRA mede STM em <strong>caracteres</strong>. Motivos:
        </p>
        <ul className="list-disc list-inside text-sm space-y-1 text-muted-foreground">
          <li>Contar token exige rodar o tokenizer do provedor — lento e específico de cada modelo.</li>
          <li>Caracteres são determinísticos, baratos e estáveis entre Gemini/GPT/Claude.</li>
          <li>
            Em PT-BR, a conversão prática é <code>1 token ≈ 3,5 a 4 caracteres</code>.
            Trabalhamos com a aproximação conservadora <strong>1 token = 4 chars</strong>.
          </li>
        </ul>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Conversão de bolso</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Caracteres</TableHead>
                  <TableHead>Tokens (aprox.)</TableHead>
                  <TableHead>O que cabe</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-mono">1.000</TableCell>
                  <TableCell>~250</TableCell>
                  <TableCell>Uma resposta longa de WhatsApp</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-mono">5.000</TableCell>
                  <TableCell>~1.250</TableCell>
                  <TableCell>~50 mensagens curtas de grupo</TableCell>
                </TableRow>
                <TableRow className="bg-primary/5">
                  <TableCell className="font-mono font-bold">24.000</TableCell>
                  <TableCell className="font-bold">~6.000</TableCell>
                  <TableCell><strong>Default da SYRA</strong> — ~250 mensagens médias / 48h</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-mono">100.000</TableCell>
                  <TableCell>~25.000</TableCell>
                  <TableCell>Conversa de uma semana inteira</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </section>

      <Separator />

      {/* Regra dos 5% */}
      <section className="space-y-4">
        <h2 className="text-2xl font-semibold flex items-center gap-2">
          <AlertTriangle className="h-6 w-6 text-primary" />
          A regra dos 5% (clamp por modelo)
        </h2>
        <p className="text-sm leading-relaxed">
          Cada modelo tem uma janela de contexto diferente. Para nunca
          estourar prompt em um modelo com janela pequena, a SYRA aplica:
        </p>

        <Card className="bg-muted/40">
          <CardContent className="pt-6">
            <code className="text-sm block text-center">
              max_chars_efetivo = min(max_chars_flag, janela_do_modelo × 0,05)
            </code>
          </CardContent>
        </Card>

        <p className="text-sm leading-relaxed">
          Os 5% são uma margem de segurança: deixam 95% da janela disponível
          para system prompt, persona, ToM, memórias longas, ferramentas,
          mídia e a resposta gerada. Se a STM consumisse 50% da janela,
          sobraria pouco para tudo o resto.
        </p>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Janelas por modelo (em caracteres)</CardTitle>
            <CardDescription>
              Valores conservadores, tokens × ~3,5 chars/token
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Modelo</TableHead>
                  <TableHead>Janela</TableHead>
                  <TableHead>5% = STM máxima</TableHead>
                  <TableHead>STM efetiva (default 24k)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-mono text-xs">google/gemini-2.5-flash</TableCell>
                  <TableCell>3.500.000</TableCell>
                  <TableCell>175.000</TableCell>
                  <TableCell><Badge variant="secondary">24.000</Badge></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-mono text-xs">openai/gpt-5</TableCell>
                  <TableCell>700.000</TableCell>
                  <TableCell>35.000</TableCell>
                  <TableCell><Badge variant="secondary">24.000</Badge></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-mono text-xs">anthropic/claude-sonnet-4-6</TableCell>
                  <TableCell>700.000</TableCell>
                  <TableCell>35.000</TableCell>
                  <TableCell><Badge variant="secondary">24.000</Badge></TableCell>
                </TableRow>
                <TableRow className="bg-warning/10">
                  <TableCell className="font-mono text-xs">vendor/desconhecido</TableCell>
                  <TableCell>480.000 (fallback)</TableCell>
                  <TableCell>24.000</TableCell>
                  <TableCell><Badge variant="outline">24.000 (no fio!)</Badge></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </section>

      <Separator />

      {/* Exemplos */}
      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">Exemplos práticos</h2>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              Exemplo 1 — Modelo grande, conversa normal
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <p><strong>Setup:</strong> grupo com 80 mensagens nas últimas 12h, modelo <code>gemini-2.5-flash</code>.</p>
            <p><strong>Cálculo:</strong> 3.500.000 × 0,05 = 175.000 chars de cap. Flag pede 24.000. Vence 24.000.</p>
            <p><strong>Resultado:</strong> entram as ~80 mensagens (provavelmente cabem em &lt;10k chars), nada é truncado.</p>
            <Badge variant="secondary">capped_by_model: false · truncated: false</Badge>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              Exemplo 2 — Conversa muito longa, modelo grande
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <p><strong>Setup:</strong> grupo com 600 mensagens em 30h, ~50 chars cada (~30k chars), modelo <code>gpt-5</code>.</p>
            <p><strong>Cálculo:</strong> max_chars=24k. Lê 300 mensagens (max_messages), monta o bloco e corta nas mais antigas até caber em 24k.</p>
            <p><strong>Resultado:</strong> ~480 mensagens entram. As 120 mais antigas são droppadas — mas continuam disponíveis via LTM.</p>
            <Badge variant="secondary">capped_by_model: false · truncated: true (por max_chars)</Badge>
          </CardContent>
        </Card>

        <Card className="border-warning/40 bg-warning/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              Exemplo 3 — Modelo desconhecido (o bug do MEM-FIX)
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <p><strong>Setup:</strong> alguém configura o agente com <code>anthropic/claude-sonnet-5</code> (modelo novo, sem entrada na whitelist).</p>
            <p><strong>Cálculo (antes do MEM-FIX):</strong> fallback era 100.000 chars × 0,05 = <strong>5.000 chars</strong>. STM caía para ~50 mensagens.</p>
            <p><strong>Cálculo (pós MEM-FIX):</strong> fallback subiu para 480.000 × 0,05 = <strong>24.000 chars</strong>, igual ao default das flags. Modelo desconhecido fica idêntico ao default.</p>
            <p><strong>Sintoma do bug:</strong> SYRA "esquecia" o que tinha sido dito 5 mensagens atrás.</p>
            <Badge variant="outline">capped_by_model: false · source_model: anthropic/claude-sonnet-5</Badge>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              Exemplo 4 — Admin pede budget alto
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <p><strong>Setup:</strong> admin sobe <code>memory.stm.max_chars</code> para 100k, modelo <code>gpt-5</code>.</p>
            <p><strong>Cálculo:</strong> min(100.000, 700.000 × 0,05) = min(100.000, 35.000) = <strong>35.000</strong>.</p>
            <p><strong>Resultado:</strong> STM efetiva é capada pelo modelo, não pela flag. <code>capped_by_model: true</code> aparece na telemetria.</p>
            <Badge variant="secondary">capped_by_model: true</Badge>
          </CardContent>
        </Card>
      </section>

      <Separator />

      {/* Operações */}
      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">Como ajustar e diagnosticar</h2>

        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Antes de mexer nas flags</AlertTitle>
          <AlertDescription className="text-xs">
            Aumentar <code>max_chars</code> sem ganho real é desperdício de
            token (custo $$$). Diminuir compromete a memória curta da SYRA.
            Use a telemetria <code>memory.stm.budget_used</code> antes.
          </AlertDescription>
        </Alert>

        <ul className="text-sm space-y-2 list-disc list-inside">
          <li>
            <strong>Widget Dashboard:</strong> "STM Budget" mostra valores
            atuais e dispara alerta se houver eventos
            <code> memory.stm.budget_starved</code> (modelo desconhecido).
          </li>
          <li>
            <strong>Logs do webhook:</strong> grep por <code>[STM-BUDGET]</code> e
            <code> [MODEL-SELECT]</code> nos logs do <code>whatsapp-webhook</code>
            para ver, por turno, o budget aplicado e o modelo escolhido.
          </li>
          <li>
            <strong>Telemetria:</strong> tabela <code>cortex_events</code>,
            event_types <code>memory.stm.budget_used</code> e
            <code> memory.stm.budget_starved</code>.
          </li>
          <li>
            <strong>Adicionar modelo novo à whitelist:</strong> editar
            <code> MODEL_CONTEXT_CHARS</code> em
            <code> supabase/functions/_shared/memory-budget.ts</code>. Os
            golden tests <code>MEM-FIX</code> garantem o piso de 24k.
          </li>
        </ul>
      </section>

      {/* Refs */}
      <section className="space-y-2 text-xs text-muted-foreground border-t pt-4">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <BookOpen className="h-4 w-4" /> Referências internas
        </h3>
        <ul className="space-y-1">
          <li><code>supabase/functions/_shared/memory-budget.ts</code> — implementação canônica</li>
          <li><code>supabase/functions/_shared/context-builder.ts</code> — consumidor</li>
          <li><code>supabase/functions/_shared/__tests__/m02-stm-budget.golden.test.ts</code> — golden tests MEM-FIX</li>
          <li><code>mem://bugs/mem-fix-stm-budget-starved</code> — post-mortem</li>
        </ul>
      </section>
    </div>
  );
}
